import type { Types as CoreTypes } from '@cornerstonejs/core';

/** 2D distance from a point to a line segment. */
export function distanceToSegment(
  a: CoreTypes.Point2,
  b: CoreTypes.Point2,
  p: CoreTypes.Point2
): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const t = Math.max(
    0,
    Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq)
  );
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

/**
 * Iterative Douglas–Peucker simplification on an N-D polyline. Distance is
 * measured against the plane spanned by the first two coordinates (the
 * canvas/world XY plane); the third coord (Z) of Point3 is preserved on the
 * kept points. Returns the kept points in original order, always including
 * the first and last.
 *
 * Iterative implementation avoids the recursion-depth blowup that plagues
 * naïve DP on long traces.
 */
export function simplifyPolylineDouglasPeucker<P extends readonly number[]>(
  points: readonly P[],
  tolerance: number
): P[] {
  if (points.length <= 2 || tolerance <= 0) return points.slice();

  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;

  // Stack of [start, end] index pairs to subdivide.
  const stack: [number, number][] = [[0, points.length - 1]];

  while (stack.length > 0) {
    const [start, end] = stack.pop() as [number, number];
    if (end - start >= 2) {
      const a: CoreTypes.Point2 = [points[start][0], points[start][1]];
      const b: CoreTypes.Point2 = [points[end][0], points[end][1]];

      let maxDist = -1;
      let maxIdx = -1;
      for (let i = start + 1; i < end; i += 1) {
        const p: CoreTypes.Point2 = [points[i][0], points[i][1]];
        const d = distanceToSegment(a, b, p);
        if (d > maxDist) {
          maxDist = d;
          maxIdx = i;
        }
      }

      if (maxDist > tolerance && maxIdx > -1) {
        keep[maxIdx] = 1;
        stack.push([start, maxIdx]);
        stack.push([maxIdx, end]);
      }
    }
  }

  const out: P[] = [];
  for (let i = 0; i < points.length; i += 1) {
    if (keep[i]) out.push(points[i]);
  }
  return out;
}

/** Cheap identity for a polyline: point count plus summed coordinates.
 *
 *  Used to tell an untouched hydrated envelope from one a reader has reshaped.
 *  Exact equality is not required — only that any real edit changes it. */
export function polylineSignature(
  points: readonly (readonly number[])[]
): string {
  let sumX = 0;
  let sumY = 0;
  points.forEach((p) => {
    sumX += p[0] ?? 0;
    sumY += p[1] ?? 0;
  });
  return `${points.length}|${sumX.toFixed(2)}|${sumY.toFixed(2)}`;
}

/**
 * Reopen a closed VTI envelope so it runs baseline → peak → baseline.
 *
 * encephalon ships a wave's envelope as `wave_regions[…].polygons[0]` — a
 * closed loop whose first and last points coincide, and whose flat edge is
 * drawn where the segmentation ended, which need not be the zero-velocity
 * line. The tool expects the shape a reader draws: an open arc with both ends
 * on the baseline. Without it the flat edge renders in the trace colour above
 * the axis, and the overlay's closing cue collapses to nothing because it spans
 * first.x → last.x.
 *
 * Drops the flat edge and extends the two cut ends onto `baselineY`, so the
 * trace reaches the axis however far short the segmentation stopped. Returns
 * the input unchanged when the loop is already open — an open trace from the
 * same payload must not be disturbed.
 *
 * `edgeTolerancePx` is how far from the envelope's own extreme still counts as
 * the flat edge; derive it from `physicalDeltaY` so it means a velocity rather
 * than a pixel count.
 */
export function openEnvelopeOnBaseline<P extends readonly number[]>(
  points: readonly P[],
  baselineY: number,
  options: { closeTolerancePx?: number; edgeTolerancePx?: number } = {}
): P[] {
  const { closeTolerancePx = 8, edgeTolerancePx = 4 } = options;
  if (points.length < 4) return [...points];

  const first = points[0];
  const last = points[points.length - 1];
  const isClosed =
    Math.hypot(
      (first[0] ?? 0) - (last[0] ?? 0),
      (first[1] ?? 0) - (last[1] ?? 0)
    ) <= closeTolerancePx;
  if (!isClosed) return [...points];

  // Farthest vertex from the baseline: the peak, and the one point guaranteed
  // to be on the arc rather than the flat edge.
  let peakIdx = 0;
  let peakDist = -1;
  points.forEach((p, i) => {
    const d = Math.abs((p[1] ?? 0) - baselineY);
    if (d > peakDist) {
      peakDist = d;
      peakIdx = i;
    }
  });
  if (peakDist <= edgeTolerancePx) return [...points];

  // The flat edge lies at the envelope's baseline-most extreme, which is NOT
  // necessarily the baseline itself — the segmentation often stops short.
  const upward = (points[peakIdx][1] ?? 0) < baselineY;
  const ys = points.map((p) => p[1] ?? 0);
  const edgeY = upward ? Math.max(...ys) : Math.min(...ys);
  const onEdge = (i: number) =>
    Math.abs((points[i][1] ?? 0) - edgeY) <= edgeTolerancePx;

  const n = points.length;
  let forwardEnd = peakIdx;
  for (let step = 1; step < n; step += 1) {
    forwardEnd = (peakIdx + step) % n;
    if (onEdge(forwardEnd)) break;
  }
  let backwardEnd = peakIdx;
  for (let step = 1; step < n; step += 1) {
    backwardEnd = (peakIdx - step + n) % n;
    if (onEdge(backwardEnd)) break;
  }
  if (forwardEnd === backwardEnd) return [...points];

  // backwardEnd → peak → forwardEnd, wrapping, so the flat edge between the
  // two is dropped.
  const arc: P[] = [];
  for (let i = backwardEnd; ; i = (i + 1) % n) {
    arc.push(points[i]);
    if (i === forwardEnd) break;
  }
  if (arc.length < 2) return [...points];

  // Extend both ends down to the axis, keeping their x so the cue spans the
  // envelope's true width at the baseline.
  const onBaseline = (p: P): P =>
    [p[0], baselineY, ...p.slice(2)] as unknown as P;
  return [onBaseline(arc[0]), ...arc, onBaseline(arc[arc.length - 1])];
}
